import { Stack } from "@mui/system";
import React from "react";
import {
  ActivityFilterComponent,
  ActivityFilterValues,
} from "@cbex/cbexcustom";
import { useWindowDimensions } from "@cbex/utils/hooks";
import dayjs from "dayjs";
import {
  GetCandidatesHistoryWithFiltersDocument,
  CbexPortalCandidatesWithFiltersRow,
  GetCertificateDocumentDataDocument,
} from "@cbex/data-access";
import { useTranslations } from "next-intl";
import { GrCertificate } from "react-icons/gr";
import { BsPass } from "react-icons/bs";
import { downLoadCommunisPDF } from "../utils";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useTheme } from "@cbex/utils/theme";
import { AppContext } from "@cbex/utils/context";
import {
  WcDataGrid,
  WcGridActionsCellItem,
  WcGridColDef,
  WcGridRowParams,
} from "@bpipartners/datagrid";
import { enableDownload } from "../utils/enableDownload";

export const CandidateHistoryOverviewComposite = () => {
  const actionStates = ["PASSED", "FAILED"];
  const [candidateDataList, setCandidateDataList] =
    React.useState<CbexPortalCandidatesWithFiltersRow[]>();
  const t = useTranslations();
  //@ts-ignore
  const { localAppState } = React.useContext(AppContext);
  const windowSize = useWindowDimensions();
  const theme = useTheme();

  const candidateColumnData: WcGridColDef[] = [
    {
      field: "id",
      headerName: t("labels.id"),
    },
    {
      field: "initials",
      headerName: t("labels.initials"),
      flex: 0.1,
      editable: false,
      sortable: false,
    },
    {
      field: "infix",
      headerName: t("labels.infix"),
      flex: 0.1,
      sortable: true,
      editable: false,
    },
    {
      field: "lastName",
      headerName: t("labels.lastName"),
      flex: 0.3,
      sortable: true,
      editable: false,
    },
    {
      field: "startDate",
      headerName: t("labels.activityDate"),
      flex: 0.2,
      sortable: false,
      editable: false,
      disableColumnMenu: true,
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) => dayjs(value).format("DD-MM-YYYY"),
    },
    {
      field: "activity",
      headerName: t("labels.activity"),
      flex: 0.4,
      sortable: false,
      editable: false,
      disableColumnMenu: true,
      valueGetter: (value, row) =>
        row.activity && row.activity.name ? row.activity.name : "",
    },

    {
      field: "dateOfBirth",
      headerName: t("labels.dob"),
      flex: 0.2,
      type: "date",
      sortable: false,
      editable: false,
      disableColumnMenu: true,
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) => dayjs(value).format("DD-MM-YYYY"),
    },
    {
      field: "emailAddress",
      headerName: t("labels.email"),
      flex: 0.2,
      sortable: false,
      editable: false,
    },
    {
      field: "notes",
      headerName: t("labels.note"),
      flex: 0.2,
      sortable: false,
      editable: false,
    },
    {
      field: "result",
      headerName: t("labels.result"),
      flex: 0.2,
      sortable: false,
      editable: false,
      valueGetter: (value: string) => (value ? t(`value.${value}`) : ""),
    },
    {
      field: "score",
      headerName: t("labels.score"),
      flex: 0.2,
      sortable: false,
      editable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: t("labels.actions"),
      align: "right",
      getActions: (params: WcGridRowParams) => [
        ...(params.row?.activity?.properties?.template &&
        params.row?.activity?.properties &&
        params.row?.activity?.properties["passTemplate"] &&
        params.row.activity.properties["passTemplate"] !== null
          ? [
              <WcGridActionsCellItem
                key={1}
                disabled={params.row.result !== "PASSED"}
                icon={
                  <BsPass
                    title={t("labels.downloadPass")}
                    size={20}
                    //@ts-ignore
                    color={
                      enableDownload(params.row)
                        ? theme.appColors.primary
                        : theme.palette.grey
                    }
                  />
                }
                label={t("labels.downloadCertificate")}
                onClick={() => {
                  handleDownloadClick(params, "pass");
                }}
              />,
            ]
          : []),
        <WcGridActionsCellItem
          key={0}
          disabled={!enableDownload(params.row)}
          icon={
            <GrCertificate
              title={t("labels.downloadCertificate")}
              size={20}
              //@ts-ignore
              color={
                enableDownload(params.row)
                  ? theme.appColors.primary
                  : theme.palette.grey
              }
            />
          }
          label={t("labels.downloadCertificate")}
          onClick={() => handleDownloadClick(params, "certificate")}
        />,
      ],
    },
  ];

  const [filterValues, setFilterValues] = React.useState<ActivityFilterValues>({
    //@ts-ignore
    dateFrom: dayjs().subtract(3, "month"),
    //@ts-ignore
    dateTill: dayjs(),
  });

  const { data: candidateData, loading: candidateLoading } = useQuery(
    GetCandidatesHistoryWithFiltersDocument,
    {
      fetchPolicy: "network-only",
      variables: {
        filter: {
          dateFrom: filterValues.dateFrom,
          dateTill: filterValues.dateTill,
        },
      },
    }
  );

  React.useEffect(() => {
    if (
      !candidateLoading &&
      candidateData &&
      candidateData.CandidatesHistoryWithFilters &&
      candidateData.CandidatesHistoryWithFilters.length > 0
    ) {
      setCandidateDataList(candidateData.CandidatesHistoryWithFilters);
    } else {
      setCandidateDataList([]);
    }
  }, [candidateData, candidateLoading]);

  const handleFilterUpdate = (data: ActivityFilterValues) => {
    setFilterValues(data);
  };

  const [getCertificateDocumentData] = useLazyQuery(
    GetCertificateDocumentDataDocument,
    {
      variables: {
        ids: [],
      },
      fetchPolicy: "network-only",
    }
  );

  const handleDownloadClick = (
    params: WcGridRowParams,
    downloadType: string
  ) => {
    getCertificateDocumentData({
      variables: {
        ids: [params.row.candidateID],
      },
    }).then((documentData) => {
      if (downloadType === "pass") {
        downLoadCommunisPDF({
          downloadName:
            "Pas " + documentData.data.CertificateDocumentData[0].fullName,
          pdfObject: {
            ...documentData.data.CertificateDocumentData[0],
            score: params.row.score,
            organization: localAppState?.userData?.organization,
          },
          templateType:
            params.row.result === "PASSED" &&
            documentData.data.CertificateDocumentData[0].activity.properties
              .passTemplate,
        });
      } else {
        downLoadCommunisPDF({
          downloadName:
            "Diploma " + documentData.data.CertificateDocumentData[0].fullName,
          pdfObject: {
            ...documentData.data.CertificateDocumentData[0],
            score: params.row.score,
          },
          templateType:
            params.row.result === "PASSED"
              ? documentData.data.CertificateDocumentData[0].activity.properties
                  .template
              : "examResultFailPDF",
        });
      }
    });
  };

  return (
    <Stack
      spacing={2}
      padding={4}
      width="100%"
      id="CandidateHistoryOverviewComposite"
    >
      <ActivityFilterComponent
        onNewPressed={() => undefined}
        isActivityOverview={false}
        inputValues={filterValues}
        onDataChanged={handleFilterUpdate}
      />
      <Stack height={windowSize.height - 160} spacing={2} width="100%">
        {!candidateLoading && candidateDataList && (
          <WcDataGrid
            initialState={{
              columns: {
                columnVisibilityModel: {
                  emailAddress: false,
                  id: false,
                  notes: false,
                },
              },
            }}
            columns={candidateColumnData}
            rowHeight={60}
            showExport
            rows={candidateDataList}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default CandidateHistoryOverviewComposite;
